import { useEffect, useState } from 'react';
import { Vote, Check, X, Ban, Hourglass, CircleCheck, Coins } from 'lucide-react';
import type { PartyJointDecision, LobbyCharacterInfo, PartyMemberInfo } from '@danda/shared';
import { VOTE_DECISION_TIMEOUT_MS, DecisionStatusEnum, VoteEnum } from '@danda/shared';
import type { Character } from '@/lib/stores/lobbyStore.types';
import { apiClient } from '@/lib/api';
import { Link } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';

interface PartyVoteBannerProps {
  decision: PartyJointDecision | undefined;
  selectedCharacterId: string | null;
  characters: Character[];
  lobbyCharacters: LobbyCharacterInfo[];
  partyMembers?: PartyMemberInfo[];
  onResolved?: (partyId: string) => void;
}

/** Decision types that require PM tokens to proceed */
const GAME_START_DECISIONS = new Set(['start-game', 'continue-game']);

export function PartyVoteBanner({
  decision,
  selectedCharacterId,
  characters,
  lobbyCharacters,
  partyMembers,
  onResolved,
}: PartyVoteBannerProps) {
  const openAccount = useAccountModalStore((s) => s.openModal);
  const status = decision?.status ?? 'pending';
  const isPending = status === DecisionStatusEnum.PENDING;
  const [isVisible, setIsVisible] = useState(true);
  const [insufficientBalance, setInsufficientBalance] = useState(false);

  // Check PM balance when a game-start decision appears
  const decisionId = decision?.id;
  const decisionType = decision?.type;
  useEffect(() => {
    if (!decisionType || !isPending || !GAME_START_DECISIONS.has(decisionType)) {
      setInsufficientBalance(false);
      return;
    }
    apiClient
      .getPmBalance()
      .then((data) => setInsufficientBalance(data.pmTokenBalance < 1))
      .catch((err) => console.error('[PartyVoteBanner] Failed to fetch PM balance:', err));
  }, [decisionId, decisionType, isPending]);

  // Auto-clear resolved decisions after a short display so UI doesn't stay stale
  useEffect(() => {
    if (!decision || !onResolved || isPending) {
      return;
    }
    // Clear after 5 seconds - long enough to see the result, short enough to not feel stale
    const timer = setTimeout(() => onResolved(decision.partyId), VOTE_DECISION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [decision, isPending, onResolved]);

  useEffect(() => {
    setIsVisible(true);
  }, [decision?.id, decision?.partyId, decision?.status]);

  if (!decision || !isVisible) return null;

  const selectedChar = characters.find((c) => c.id === selectedCharacterId);
  // Rust backend sends aggregate counts in metadata; legacy TS backend uses individual vote objects
  const yesVotes =
    typeof decision.metadata?.votesYes === 'number'
      ? decision.metadata.votesYes
      : decision.votes.filter((v) => v.vote === VoteEnum.YES).length;
  const noVotes =
    typeof decision.metadata?.votesNo === 'number'
      ? decision.metadata.votesNo
      : decision.votes.filter((v) => v.vote === VoteEnum.NO).length;
  // Rust backend tracks voter IDs in metadata; legacy TS backend uses individual vote objects
  const votedCharIds = Array.isArray(decision.metadata?.votedCharIds)
    ? (decision.metadata.votedCharIds as string[])
    : [];
  const hasVoted = selectedChar
    ? decision.votes.some((v) => v.characterId === selectedChar.id) ||
      votedCharIds.includes(selectedChar.id)
    : false;

  // Helper to get character name from ID
  const getCharacterName = (charId: string): string => {
    // Check user's own characters first
    const ownChar = characters.find((c) => c.id === charId);
    if (ownChar) return ownChar.name;
    // Check party members (includes NPCs in the party)
    const partyMember = partyMembers?.find((m) => m.id === charId);
    if (partyMember) return partyMember.name;
    // Check lobby characters (NPCs and other players in lobby)
    const lobbyChar = lobbyCharacters.find((c) => c.id === charId);
    if (lobbyChar) return lobbyChar.name;
    // Fallback to ID
    return charId;
  };

  // Use individual vote objects (legacy TS) or voter IDs from metadata (Rust backend)
  const voterNames =
    decision.votes.length > 0
      ? decision.votes.map((v) => getCharacterName(v.characterId)).join(', ')
      : votedCharIds.map((id) => getCharacterName(id)).join(', ');

  const statusDisplay = (() => {
    switch (status) {
      case 'passed':
        return {
          icon: CircleCheck,
          text: 'Vote Passed',
          tone: 'text-green-600 dark:text-green-400',
        };
      case 'failed':
        return { icon: X, text: 'Vote Failed', tone: 'text-red-600 dark:text-red-400' };
      case 'expired':
        return {
          icon: Hourglass,
          text: 'Vote Expired',
          tone: 'text-amber-600 dark:text-amber-400',
        };
      case 'cancelled':
        return { icon: Ban, text: 'Vote Cancelled', tone: 'text-muted-foreground' };
      default:
        return { icon: Vote, text: 'Vote in Progress', tone: 'text-primary' };
    }
  })();

  const StatusIcon = statusDisplay.icon;

  return (
    <div className="bg-accent/50 text-accent-foreground p-3 rounded-md mb-2 text-sm flex flex-col gap-2 border border-accent relative">
      <button
        type="button"
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss vote banner"
        onClick={() => setIsVisible(false)}
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2 pr-6">
        <StatusIcon className={`h-4 w-4 ${statusDisplay.tone}`} />
        <span className="font-medium">
          {statusDisplay.text}: {decision.description}
        </span>
      </div>
      <div className="flex justify-between items-center text-xs text-muted-foreground">
        <span>Required: {decision.votesRequired}</span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <Check className="h-3.5 w-3.5" /> {yesVotes}
          </span>
          <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <X className="h-3.5 w-3.5" /> {noVotes}
          </span>
        </span>
      </div>
      <div className="text-xs text-muted-foreground">Voters: {voterNames || 'None'}</div>
      {isPending ? (
        <>
          {insufficientBalance && (
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-destructive font-medium">
              <Coins className="h-3.5 w-3.5" />
              Out of PMT.{' '}
              <button
                type="button"
                onClick={() => {
                  track('invite_cta_click', { surface: 'party_vote_banner', low_balance: true });
                  openAccount('invite');
                }}
                className="underline hover:text-destructive/80"
              >
                Invite friends
              </button>
              <span className="text-muted-foreground">·</span>
              <Link to="/buy-tokens" className="underline hover:text-destructive/80">
                Buy tokens
              </Link>
              <span className="text-muted-foreground">·</span>
              <button
                type="button"
                onClick={() => {
                  track('earn_more_cta_click', { surface: 'party_vote_banner', low_balance: true });
                  openAccount('profile');
                }}
                className="text-muted-foreground underline hover:text-foreground"
              >
                Additional ways to earn
              </button>
            </div>
          )}
          {hasVoted && (
            <div className="text-xs text-muted-foreground italic">You have already voted.</div>
          )}
          {!hasVoted && !insufficientBalance && (
            <div className="text-xs text-foreground/80">Type 'yes' or 'no' to cast your vote.</div>
          )}
        </>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          Final tally: {yesVotes} yes, {noVotes} no.
        </div>
      )}
    </div>
  );
}
